import classNames from 'classnames';
import type Hls from 'hls.js';
import { noop } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMountedState } from 'react-use';

import { type Clip } from '@air/api/types';
import { ClipUtils } from '@air/utils-clips';
import { reportErrorToBugsnag } from '@air/utils-error';
import { getVideoFormatFromUrl, isHlsSupported, loadHls } from '@air/utils-video';

export type ClipPreviewProps = {
  vertical: boolean;
  src: string;
  playbackRate: number;
} & Pick<Clip, 'status'>;

export const ClipPreview = ({ status, vertical, src, playbackRate }: ClipPreviewProps) => {
  const isMounted = useMountedState();
  const hlsRef = useRef<Hls>();
  const ref = useRef<HTMLVideoElement>(null);
  const playPromise = useRef<Promise<void>>(Promise.resolve());
  const isPlaying = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const transcoded = ClipUtils.isTranscoded(status);

  // Implementation needed to avoid an exception.
  // https://stackoverflow.com/questions/36803176/how-to-prevent-the-play-request-was-interrupted-by-a-call-to-pause-error/40370077#40370077
  const playVideo = useCallback(() => {
    if (ref.current?.paused && !isPlaying.current) {
      // catch and do nothing to ignore this error
      // https://developers.google.com/web/updates/2017/06/play-request-was-interrupted
      playPromise.current = ref.current.play()?.catch(noop);
    }
  }, []);

  useEffect(() => {
    if (loaded && ref.current) {
      playVideo();
    }
  }, [loaded, playVideo]);

  useEffect(() => {
    const loadVideo = async () => {
      if (ref.current) {
        ref.current.playbackRate = playbackRate;
        ref.current.onplaying = () => {
          isPlaying.current = true;
        };
        ref.current.onpause = () => {
          isPlaying.current = false;
        };
      }

      const fmt = getVideoFormatFromUrl(src);
      if (fmt !== 'm3u8') {
        if (ref.current) {
          ref.current.src = src || '';
          //@ts-expect-error video doesn't have a type prop?
          ref.current.type = `video/${fmt}`;
        }
        setLoaded(true);
        return;
      }

      if (!isHlsSupported()) {
        setLoaded(true);
        return;
      }

      try {
        hlsRef.current?.destroy();
        hlsRef.current = await loadHls({ src, videoElement: ref.current });
        if (isMounted()) {
          setLoaded(true);
        } else {
          return;
        }
      } catch (error) {
        reportErrorToBugsnag({
          error,
          context: 'Failed to load new Hls source in ClipPreview',
          metadata: {
            key: 'Data',
            data: { src },
          },
        });
      }
    };

    loadVideo();
  }, [playbackRate, src, isMounted]);

  useEffect(() => {
    return () => {
      hlsRef.current?.destroy();
    };
  }, []);

  return (
    <video
      className={classNames(
        'pointer-events-none absolute top-0 left-0 h-full w-full rounded-4 bg-black',
        vertical ? 'object-contain' : 'object-cover',
        transcoded ? 'block' : 'hidden',
      )}
      muted
      loop
      playsInline
      ref={ref}
      src={src}
    />
  );
};
